// Pure core of useTranslatedResponses: no React, axios or timers, so the loop can be exercised
// in node with injected process/read/wait fakes.

export const TRANSLATE_MODE_ORIGINAL = "ORIGINAL";
export const TRANSLATE_MODE_ENGLISH = "ENGLISH";

export const TRANSLATE_STATUS_IDLE = "idle";
export const TRANSLATE_STATUS_RUNNING = "running";
export const TRANSLATE_STATUS_DONE = "done";
export const TRANSLATE_STATUS_ERROR = "error";

export const TRANSLATE_ERROR_STALE = "STALE";
export const TRANSLATE_ERROR_ANONYMITY = "ANONYMITY";
export const TRANSLATE_ERROR_DISABLED = "DISABLED";
export const TRANSLATE_ERROR_UNAVAILABLE = "UNAVAILABLE";
export const TRANSLATE_ERROR_GENERIC = "GENERIC";

export const TRANSLATE_NOTICE_BUSY = "BUSY";
export const TRANSLATE_NOTICE_RATE_LIMITED = "RATE_LIMITED";

export const TRANSLATE_OUTCOME_DONE = "done";
export const TRANSLATE_OUTCOME_ERROR = "error";
export const TRANSLATE_OUTCOME_REVERT = "revert";
export const TRANSLATE_OUTCOME_CANCELLED = "cancelled";

// A process call may use the BE's 25 s budget plus a Claude retry, so allow more than that.
export const TRANSLATE_REQUEST_TIMEOUT_MS = 45000;
export const TRANSLATE_READ_CHUNK = 200;
export const TRANSLATE_MAX_STRIKES = 3;
export const TRANSLATE_MAX_WAITS = 30;
export const TRANSLATE_MAX_BATCHES = 2000;
export const TRANSLATE_MAX_FOLLOW_UPS = 2;
export const TRANSLATE_ERROR_WAIT_MS = 2000;
export const TRANSLATE_BUSY_WAIT_MS = 2000;
export const TRANSLATE_RATE_LIMIT_WAIT_MS = 5000;
export const TRANSLATE_FOLLOW_UP_WAIT_MS = 5000;
export const TRANSLATE_REFRESH_INTERVAL_MS = 3000;
// Each extra chunk of rows to read stretches the gap between refreshes, keeping reads bounded.
export const TRANSLATE_REFRESH_MS_PER_CHUNK = 500;
const TRANSLATE_MAX_SERVER_WAIT_MS = 60000;

// The BE keys a Message answer with this reserved question id; the FE keys it by result id alone.
const MESSAGE_QUESTION_ID = "__message__";
const STATUS_TRANSLATED = "TRANSLATED";
// Answers that cannot change again within a pass; their rows are not re-read.
const SETTLED_STATUSES = ["TRANSLATED", "ALREADY_ENGLISH", "SKIPPED"];
const STALE_BODY_PATTERN = /Cannot (POST|GET)/i;
const UNAVAILABLE_CODES = ["NOT_AUTHORIZED", "TRIVVY_NOT_FOUND", "UNAUTHENTICATED", "MISSING_ORGANIZATION"];

// Messages keys are "<resultId>"; Survey/KC keys are "<resultId>:<questionId>".
export const buildTextKey = (resultId, questionId) => {
  if (questionId && questionId !== MESSAGE_QUESTION_ID) {
    return `${resultId}:${questionId}`;
  }
  return `${resultId}`;
};

// Order-independent signature of a set of result ids (a re-sort is not a new set of rows).
export const signatureOf = (ids) => ids.slice().sort().join(",");

// Returns the English text for a row/answer, or `original` when none exists.
export const pickTranslatedText = (map, resultId, questionId, original) => {
  if (!map) {
    return original;
  }
  const text = map[buildTextKey(resultId, questionId)];
  if (typeof text === "string" && text.length > 0) {
    return text;
  }
  return original;
};

// Applies read rows to the English map. Only TRANSLATED answers carry English; any other status
// (pending, edited, failed) removes a stale entry. Returns how many keys changed.
export const mergeTranslations = (target, translations) => {
  let changed = 0;
  if (!target || !Array.isArray(translations)) {
    return changed;
  }
  translations.forEach((item) => {
    if (!item || typeof item.resultId !== "string") {
      return;
    }
    const key = buildTextKey(item.resultId, item.questionId);
    const english =
      item.status === STATUS_TRANSLATED && typeof item.englishText === "string" && item.englishText.length > 0
        ? item.englishText
        : null;
    if (english !== null) {
      if (target[key] !== english) {
        target[key] = english;
        changed += 1;
      }
      return;
    }
    if (Object.prototype.hasOwnProperty.call(target, key)) {
      delete target[key];
      changed += 1;
    }
  });
  return changed;
};

export const chunkIds = (ids, size) => {
  const chunks = [];
  for (let at = 0; at < ids.length; at += size) {
    chunks.push(ids.slice(at, at + size));
  }
  return chunks;
};

// A row is settled once every answer it returned is final; a row with no answers is settled too.
const markSettled = (settledIds, requestedIds, translations) => {
  const statusesById = {};
  (Array.isArray(translations) ? translations : []).forEach((item) => {
    if (item && typeof item.resultId === "string") {
      statusesById[item.resultId] = (statusesById[item.resultId] || []).concat(item.status);
    }
  });
  requestedIds.forEach((id) => {
    const statuses = statusesById[id] || [];
    if (statuses.every((status) => SETTLED_STATUSES.indexOf(status) !== -1)) {
      settledIds.add(id);
    }
  });
};

const errorsOf = (err) => {
  const data = err && err.response && err.response.data;
  if (data && typeof data === "object" && data.errors && typeof data.errors === "object") {
    return data.errors;
  }
  return null;
};

// Retry-After header first, then the body hints, capped so a bad value never parks the loop.
const serverWaitMs = (err, fallbackMs) => {
  const headers = err && err.response && err.response.headers;
  const errors = errorsOf(err);
  let ms = 0;
  const header = headers ? Number(headers["retry-after"]) : 0;
  if (header > 0) {
    ms = header * 1000;
  } else if (errors && Number(errors.retryAfterSeconds) > 0) {
    ms = Number(errors.retryAfterSeconds) * 1000;
  } else if (errors && errors.progress && Number(errors.progress.retryAfterMs) > 0) {
    ms = Number(errors.progress.retryAfterMs);
  }
  if (ms > 0) {
    return Math.min(ms, TRANSLATE_MAX_SERVER_WAIT_MS);
  }
  return fallbackMs;
};

// HTML "Cannot POST/GET" = stale BE deploy; otherwise the JSON errors.code decides what happens.
export const classifyTranslateError = (err) => {
  const response = err && err.response;
  if (!response) {
    return { kind: TRANSLATE_ERROR_GENERIC, retryable: true, strike: true, waitMs: TRANSLATE_ERROR_WAIT_MS };
  }
  if (typeof response.data === "string" && STALE_BODY_PATTERN.test(response.data)) {
    return { kind: TRANSLATE_ERROR_STALE, retryable: false, revert: true };
  }
  const errors = errorsOf(err);
  const code = errors ? errors.code || errors.status : null;
  if (code === "ANONYMITY_THRESHOLD") {
    return { kind: TRANSLATE_ERROR_ANONYMITY, retryable: false, revert: true };
  }
  if (code === "TRANSLATION_DISABLED") {
    return { kind: TRANSLATE_ERROR_DISABLED, retryable: false, revert: true };
  }
  if (UNAVAILABLE_CODES.indexOf(code) !== -1) {
    return { kind: TRANSLATE_ERROR_UNAVAILABLE, retryable: false, revert: true };
  }
  if (response.status === 429) {
    return { kind: TRANSLATE_ERROR_GENERIC, retryable: true, strike: false, notice: TRANSLATE_NOTICE_RATE_LIMITED, waitMs: serverWaitMs(err, TRANSLATE_RATE_LIMIT_WAIT_MS) };
  }
  if (code === "BUSY") {
    return { kind: TRANSLATE_ERROR_GENERIC, retryable: true, strike: false, notice: TRANSLATE_NOTICE_BUSY, waitMs: serverWaitMs(err, TRANSLATE_BUSY_WAIT_MS) };
  }
  if (code === "MODEL_UNAVAILABLE") {
    return { kind: TRANSLATE_ERROR_GENERIC, retryable: true, strike: true, notice: TRANSLATE_NOTICE_RATE_LIMITED, waitMs: serverWaitMs(err, TRANSLATE_RATE_LIMIT_WAIT_MS) };
  }
  if (code === "INVALID_CURSOR") {
    return { kind: TRANSLATE_ERROR_GENERIC, retryable: true, restart: true };
  }
  // Claude rejected the request itself (bad key, bad input): retrying the same call cannot help.
  if (code === "MODEL_ERROR") {
    return { kind: TRANSLATE_ERROR_GENERIC, retryable: false };
  }
  const retryable = response.status >= 500 || response.status === 408;
  return { kind: TRANSLATE_ERROR_GENERIC, retryable: retryable, strike: true, waitMs: TRANSLATE_ERROR_WAIT_MS };
};

const emptyTotals = () => ({ checked: 0, translated: 0, failed: 0, pending: 0 });

export const createRunState = () => ({
  cursor: null,
  totals: emptyTotals(),
  strikes: 0,
  waits: 0,
  batches: 0,
  noProgress: 0,
  followUps: 0,
  restarted: false,
  passComplete: false,
  noticeShown: false,
  lastRefreshAt: 0,
  lastReadSignature: null,
  settledIds: new Set(),
});

const toCount = (value) => {
  if (typeof value === "number" && isFinite(value) && value > 0) {
    return value;
  }
  return 0;
};

// A batch reports its own new outcomes plus the cached answers it walked past. Returns its real
// progress (new outcomes, other admins' answers, results passed), never counting cached re-reads.
const addBatch = (totals, batch) => {
  const cached = batch.cached && typeof batch.cached === "object" ? batch.cached : {};
  const cachedCount =
    toCount(cached.translated) + toCount(cached.alreadyEnglish) + toCount(cached.skipped) + toCount(cached.failed);
  totals.checked += toCount(batch.processed) + cachedCount;
  totals.translated += toCount(batch.translated) + toCount(cached.translated);
  totals.failed += toCount(batch.failed) + toCount(cached.failed);
  totals.pending += toCount(batch.pending);
  return toCount(batch.processed) + toCount(batch.pending) + toCount(batch.scannedResults);
};

const snapshot = (state) => Object.assign({}, state.totals);

const errorOutcome = () => ({
  outcome: TRANSLATE_OUTCOME_ERROR,
  kind: TRANSLATE_ERROR_GENERIC,
});

const CANCELLED = { outcome: TRANSLATE_OUTCOME_CANCELLED };

// One English pass: read what is cached for the rows on screen, then process one batch per call
// until hasMore is false. `state` is mutated, so calling again (Retry) resumes from its cursor.
export const runTranslatePass = async (deps, state) => {
  const { process, read, getVisibleIds, now, wait, isCancelled, onUpdate } = deps;
  state.strikes = 0;
  state.waits = 0;
  state.batches = 0;
  state.noProgress = 0;
  state.restarted = false;

  // One request with bounded waits and strikes: { value }, { stop } or { restart }.
  const attempt = async (request) => {
    while (true) {
      if (isCancelled()) {
        return { stop: CANCELLED };
      }
      try {
        const value = await request();
        if (isCancelled()) {
          return { stop: CANCELLED };
        }
        if (!value || typeof value !== "object") {
          throw new Error("Invalid translation response");
        }
        state.strikes = 0;
        state.waits = 0;
        if (state.noticeShown) {
          state.noticeShown = false;
          onUpdate({ notice: null });
        }
        return { value: value };
      } catch (err) {
        if (isCancelled()) {
          return { stop: CANCELLED };
        }
        const info = classifyTranslateError(err);
        if (info.revert) {
          return { stop: { outcome: TRANSLATE_OUTCOME_REVERT, kind: info.kind } };
        }
        if (!info.retryable) {
          return { stop: errorOutcome() };
        }
        if (info.restart) {
          state.cursor = null;
          if (state.restarted) {
            return { stop: errorOutcome() };
          }
          state.restarted = true;
          return { restart: true };
        }
        if (info.strike) {
          state.strikes += 1;
          if (state.strikes >= TRANSLATE_MAX_STRIKES) {
            return { stop: errorOutcome() };
          }
        }
        state.waits += 1;
        if (state.waits > TRANSLATE_MAX_WAITS) {
          return { stop: errorOutcome() };
        }
        state.noticeShown = Boolean(info.notice);
        onUpdate({ notice: info.notice || null });
        await wait(info.waitMs || TRANSLATE_ERROR_WAIT_MS);
      }
    }
  };

  // Current answers for the rows on screen that are not settled yet, in chunks of 200.
  const refresh = async () => {
    state.lastRefreshAt = now();
    const visible = getVisibleIds();
    const unsettled = visible.filter((id) => !state.settledIds.has(id));
    const chunks = chunkIds(unsettled, TRANSLATE_READ_CHUNK);
    for (let at = 0; at < chunks.length; at += 1) {
      const result = await attempt(() => read(chunks[at]));
      if (result.stop) {
        return result.stop;
      }
      if (result.value) {
        onUpdate({ translations: result.value.translations });
        markSettled(state.settledIds, chunks[at], result.value.translations);
      }
    }
    state.lastReadSignature = signatureOf(visible);
    return null;
  };

  const refreshDue = () => {
    const unsettled = getVisibleIds().filter((id) => !state.settledIds.has(id)).length;
    const gap = Math.max(
      TRANSLATE_REFRESH_INTERVAL_MS,
      Math.ceil(unsettled / TRANSLATE_READ_CHUNK) * TRANSLATE_REFRESH_MS_PER_CHUNK
    );
    return unsettled > 0 && now() - state.lastRefreshAt >= gap;
  };

  onUpdate({ status: TRANSLATE_STATUS_RUNNING, notice: null, progress: snapshot(state) });

  // A Retry after only the final read failed: the pass itself already finished.
  if (state.passComplete) {
    const stop = await refresh();
    if (stop) {
      return stop;
    }
    return { outcome: TRANSLATE_OUTCOME_DONE };
  }

  let stop = await refresh();
  if (stop) {
    return stop;
  }

  while (true) {
    if (state.batches >= TRANSLATE_MAX_BATCHES) {
      return errorOutcome();
    }
    const result = await attempt(() => process(state.cursor));
    if (result.stop) {
      return result.stop;
    }
    if (result.restart) {
      state.totals = emptyTotals();
      continue;
    }
    const batch = result.value;
    state.batches += 1;
    const moved = addBatch(state.totals, batch);
    const hasMore = batch.hasMore === true;
    // The cursor moves before any read, so a failed read never makes Retry redo this batch.
    state.cursor = hasMore && typeof batch.nextCursor === "string" && batch.nextCursor ? batch.nextCursor : null;
    onUpdate({ progress: snapshot(state) });
    if (hasMore) {
      // A batch that moved nothing is a strike, so a stuck server can never spin the loop.
      if (moved === 0) {
        state.noProgress += 1;
        if (state.noProgress >= TRANSLATE_MAX_STRIKES) {
          return errorOutcome();
        }
        await wait(TRANSLATE_ERROR_WAIT_MS);
        continue;
      }
      state.noProgress = 0;
      if (toCount(batch.translated) > 0 && refreshDue()) {
        stop = await refresh();
        if (stop) {
          return stop;
        }
      }
      continue;
    }
    // Another admin's request still holds some answers: look again a bounded number of times.
    if (state.totals.pending > 0 && state.followUps < TRANSLATE_MAX_FOLLOW_UPS) {
      state.followUps += 1;
      await wait(TRANSLATE_FOLLOW_UP_WAIT_MS);
      if (isCancelled()) {
        return CANCELLED;
      }
      state.totals = emptyTotals();
      continue;
    }
    state.passComplete = true;
    stop = await refresh();
    if (stop) {
      return stop;
    }
    return { outcome: TRANSLATE_OUTCOME_DONE };
  }
};

// Read-only refresh for rows that appear after a pass (filters, a new page): never any AI work.
export const readVisibleTranslations = async (read, ids, isCancelled, onTranslations) => {
  const chunks = chunkIds(ids, TRANSLATE_READ_CHUNK);
  for (let at = 0; at < chunks.length; at += 1) {
    if (isCancelled()) {
      return;
    }
    const page = await read(chunks[at]);
    if (isCancelled()) {
      return;
    }
    onTranslations(page && page.translations);
  }
};
